/**
 * Sidebar Reserved Item Expiration filter: binds RmaListFiltersStore reserved-expiration draft
 * to RmaListFilterControl via OPTIONS (descriptions + nested date/number range content).
 */
import { observer } from "mobx-react";
import type { ReactNode } from "react";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";
import { RESERVED_ITEM_EXPIRATION_TYPE } from "../../../../../services/api/rma-list-service";
import rmsMixpanelStore from "../../../../../stores/rms-mixpanel-store";
import RmaListFiltersStore from "../../../rma-list-page/stores/rma-list-filters-store";
import {
    IRmaListFilterControlOption,
    RMA_LIST_FILTER_CONTROL_DATA_TYPE,
    RmaListFilterOptionId,
} from "../rma-list-filter-control/rma-list-filter-control-portal/rma-list-filter-control-content/rma-list-filter-control-content";
import RmaListFilterControl from "../rma-list-filter-control/rma-list-filter-control";
import { getReservedItemSelectedLabel } from "./get-reserved-item-selected-label";
import ReservedItemDateRangeContent from "./reserved-item-date-range-content/reserved-item-date-range-content";
import ReservedItemDaysRemainingContent from "./reserved-item-days-remaining-content/reserved-item-days-remaining-content";

interface IProps {
    rmaListFiltersStore: RmaListFiltersStore;
    disabled?: boolean;
}

const OPTION_DESCRIPTION_KEYS: Record<RESERVED_ITEM_EXPIRATION_TYPE, string> = {
    [RESERVED_ITEM_EXPIRATION_TYPE.ALL]: "RESERVED_ITEM_EXPIRATION_ALL_DESCRIPTION",
    [RESERVED_ITEM_EXPIRATION_TYPE.EXPIRED]: "RESERVED_ITEM_EXPIRATION_EXPIRED_DESCRIPTION",
    [RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING]: "RESERVED_ITEM_EXPIRATION_DAYS_REMAINING_DESCRIPTION",
    [RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE]: "RESERVED_ITEM_EXPIRATION_DATE_RANGE_DESCRIPTION",
};

const getContextualFields = (
    id: RESERVED_ITEM_EXPIRATION_TYPE,
    rmaListFiltersStore: RmaListFiltersStore,
    timeZone: string,
): ReactNode | undefined => {
    switch (id) {
        case RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING:
            return <ReservedItemDaysRemainingContent rmaListFiltersStore={rmaListFiltersStore} />;
        case RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE:
            return <ReservedItemDateRangeContent rmaListFiltersStore={rmaListFiltersStore} timeZone={timeZone}/>;
        default:
            return undefined;
    }
};

const RmaListFilterReservedItem: React.FC<IProps> = observer(({ rmaListFiltersStore, disabled }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const { timeZone } = useDateTimeFormattingParams();
    const selectedId = rmaListFiltersStore.reserveItemExpiration;
    const daysStore = rmaListFiltersStore.reservedItemDaysRemainingStore;

    const clearReservedItemFilter = () => {
        rmaListFiltersStore.setReserverdItemExpiration(RESERVED_ITEM_EXPIRATION_TYPE.ALL);
        rmaListFiltersStore.setReserverdItemExpirationInput("");
        daysStore.reset();
    };

    const handleChange = (id: RmaListFilterOptionId) => {
        const next = id as RESERVED_ITEM_EXPIRATION_TYPE;
        if (next === selectedId) {
            return;
        }
        rmaListFiltersStore.setReserverdItemExpiration(next);
        rmaListFiltersStore.setReserverdItemExpirationInput("");
        if (next !== RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING) {
            daysStore.reset();
        }
    };

    const options: IRmaListFilterControlOption[] = rmaListFiltersStore.reserverdItemExpirationOptions.map(
        (option) => {
            const id = option.value as RESERVED_ITEM_EXPIRATION_TYPE;
            return {
                id,
                label: option.label,
                description: getTranslation(OPTION_DESCRIPTION_KEYS[id], scope),
                contextualFields: getContextualFields(id, rmaListFiltersStore, timeZone),
            };
        },
    );

    const selectedLabel = getReservedItemSelectedLabel({
        selectedId,
        expiredLabel: options.find((option) => option.id === selectedId)?.label,
        daysFrom: daysStore.from,
        daysTo: daysStore.to,
        dateRangeIso: rmaListFiltersStore.reserveItemExpirationInput || "",
        timeZone,
    });

    return (
        <RmaListFilterControl
            label={getTranslation("RESERVED_ITEM_EXPIRATION", scope)}
            selectedLabel={selectedLabel}
            disabled={disabled}
            onOpen={() => rmsMixpanelStore.trackEvent("d&_filter_reserved_item_expiration")}
            onClear={clearReservedItemFilter}
            filterControlDataProps={{
                type: RMA_LIST_FILTER_CONTROL_DATA_TYPE.OPTIONS,
                value: selectedId,
                options,
                onChange: handleChange,
            }}
        />
    );
});

export default RmaListFilterReservedItem;
